import _slicedToArray from '@babel/runtime/helpers/esm/slicedToArray';
import _objectSpread from '@babel/runtime/helpers/esm/objectSpread2';
import { invariant, ErrorConstant, isFunction, hasTransactionChanged, isElementDomNode, getStyle, within, isSelectionEmpty, findParentNode, isEmptyBlockNode, getDefaultBlockNode, isTextSelection, getSelectedWord, getTextSelection, getMarkRanges, extension, command, helper, isString, PlainExtension, debounce } from '@remirror/core';
export { isEmptyBlockNode } from '@remirror/core';
import _createForOfIteratorHelper from '@babel/runtime/helpers/esm/createForOfIteratorHelper';
import _defineProperty from '@babel/runtime/helpers/esm/defineProperty';
import _classPrivateFieldSet from '@babel/runtime/helpers/esm/classPrivateFieldSet';
import _classPrivateFieldGet from '@babel/runtime/helpers/esm/classPrivateFieldGet';
import { createNanoEvents } from 'nanoevents';
import _applyDecoratedDescriptor from '@babel/runtime/helpers/esm/applyDecoratedDescriptor';
import { DecorationSet, Decoration } from '@remirror/pm/view';
import { ExtensionPositionerTheme } from '@remirror/theme';

function _checkPrivateRedeclaration(obj, privateCollection) {
  if (privateCollection.has(obj)) {
    throw new TypeError("Cannot initialize the same private elements twice on an object");
  }
}

function _classPrivateFieldInitSpec(obj, privateMap, value) {
  _checkPrivateRedeclaration(obj, privateMap);
  privateMap.set(obj, value);
}

var _handler = /*#__PURE__*/new WeakMap();

var _active = /*#__PURE__*/new WeakMap();

var _props = /*#__PURE__*/new WeakMap();

var _ids = /*#__PURE__*/new WeakMap();

var _updated = /*#__PURE__*/new WeakMap();

var _constructorProps = /*#__PURE__*/new WeakMap();

var _getActive = /*#__PURE__*/new WeakMap();

var _getID = /*#__PURE__*/new WeakMap();

var _getPosition = /*#__PURE__*/new WeakMap();

/**
 * This is the positioner. It exists to report the position of things in the
 * editor. Typically you will use it to get the position of the cursor.
 *
 * But you can be more ambitious and get the position all the active nodes of a
 * certain type. Or all visible nodes of a certain type in the editor, updated
 * as it scrolls.
 *
 * The positions returned have a rect which is the viewport position.
 *
 * There are also the `top`, `left`, `right`, `bottom` which represent the
 * absolute positioned rectangle of the position in questions. For a cursor
 * position `left` and `right` are probably the same.
 */
class Positioner {
  /**
   * An empty return value for the positioner.
   */

  /**
   * Create a positioner.
   */
  static create(props) {
    return new Positioner(props);
  }
  /**
   * Create a positioner from an existing positioner.
   *
   * This is useful when you want to modify parts of the positioner.
   */


  static fromPositioner(positioner, base) {
    return Positioner.create(_objectSpread(_objectSpread({}, positioner.basePositioner), base));
  }

  get basePositioner() {
    return {
      getActive: _classPrivateFieldGet(this, _getActive),
      getPosition: _classPrivateFieldGet(this, _getPosition),
      hasChanged: this.hasChanged,
      events: this.events,
      getID: _classPrivateFieldGet(this, _getID)
    };
  }

  constructor(props) {
    var _props$events;

    _classPrivateFieldInitSpec(this, _handler, {
      writable: true,
      value: createNanoEvents()
    });

    _classPrivateFieldInitSpec(this, _active, {
      writable: true,
      value: []
    });

    _classPrivateFieldInitSpec(this, _props, {
      writable: true,
      value: new Map()
    });

    _classPrivateFieldInitSpec(this, _ids, {
      writable: true,
      value: []
    });

    _classPrivateFieldInitSpec(this, _updated, {
      writable: true,
      value: false
    });

    _classPrivateFieldInitSpec(this, _constructorProps, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _getActive, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _getID, {
      writable: true,
      value: void 0
    });

    _classPrivateFieldInitSpec(this, _getPosition, {
      writable: true,
      value: void 0
    });

    _defineProperty(this, "addListener", (event, cb) => {
      return _classPrivateFieldGet(this, _handler).on(event, cb);
    });

    _classPrivateFieldSet(this, _constructorProps, props);

    _classPrivateFieldSet(this, _getActive, props.getActive);

    _classPrivateFieldSet(this, _getPosition, props.getPosition);

    _classPrivateFieldSet(this, _getID, props.getID);

    this.hasChanged = props.hasChanged;
    this.events = (_props$events = props.events) !== null && _props$events !== void 0 ? _props$events : ['state', 'scroll'];
  }
  /**
   * Get the active element setters.
   */


  onActiveChanged(props) {
    var _this = this;

    this.recentUpdate = props;

    var active = _classPrivateFieldGet(this, _getActive).call(this, props);

    _classPrivateFieldSet(this, _active, active);

    _classPrivateFieldSet(this, _props, new Map());

    _classPrivateFieldSet(this, _updated, false);

    _classPrivateFieldSet(this, _ids, []);

    var elementSetters = [];

    var _iterator = _createForOfIteratorHelper(active.entries()),
        _step;

    try {
      var _loop = function _loop() {
        var _step$value = _slicedToArray(_step.value, 2),
            index = _step$value[0],
            data = _step$value[1];

        var id = _this.getID(data, index);

        _classPrivateFieldGet(_this, _ids).push(id);

        elementSetters.push({
          setElement: element => {
            return _this.addProps(_objectSpread(_objectSpread({}, props), {}, {
              data,
              element
            }), index);
          },
          id
        });
      };

      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        _loop();
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }

    _classPrivateFieldGet(this, _handler).emit('update', elementSetters);
  }
  /**
   * Get the id for the active data. Defaults to the index of the data item.
   */


  getID(data, index) {
    var _classPrivateFieldGet2, _classPrivateFieldGet3;

    return (_classPrivateFieldGet2 = (_classPrivateFieldGet3 = _classPrivateFieldGet(this, _getID)) === null || _classPrivateFieldGet3 === void 0 ? void 0 : _classPrivateFieldGet3.call(this, data, index)) !== null && _classPrivateFieldGet2 !== void 0 ? _classPrivateFieldGet2 : index.toString();
  }
  /**
   * Add a listener to the positioner events.
   */


  addProps(props, index) {
    if (_classPrivateFieldGet(this, _updated)) {
      return;
    }

    _classPrivateFieldGet(this, _props).set(index, props);

    if (_classPrivateFieldGet(this, _props).size < _classPrivateFieldGet(this, _active).length) {
      return;
    }

    var doneProps = [];

    var _iterator2 = _createForOfIteratorHelper(_classPrivateFieldGet(this, _active).keys()),
        _step2;

    try {
      for (_iterator2.s(); !(_step2 = _iterator2.n()).done;) {
        var _index = _step2.value;

        var item = _classPrivateFieldGet(this, _props).get(_index);

        !item ? process.env.NODE_ENV !== "production" ? invariant(false, {
          code: ErrorConstant.INTERNAL,
          message: 'Something went wrong when retrieving the parameters'
        }) : invariant(false) : void 0;

        var id = _classPrivateFieldGet(this, _ids)[_index];

        if (!id) {
          return;
        }

        doneProps.push({
          position: _classPrivateFieldGet(this, _getPosition).call(this, item),
          element: item.element,
          id
        });
      }
    } catch (err) {
      _iterator2.e(err);
    } finally {
      _iterator2.f();
    }

    _classPrivateFieldGet(this, _handler).emit('done', doneProps);
  }
  /**
   * Create a new Positioner with the provided props.
   */


  clone(props) {
    return Positioner.create(_objectSpread(_objectSpread({}, _classPrivateFieldGet(this, _constructorProps)), isFunction(props) ? props(_classPrivateFieldGet(this, _constructorProps)) : props));
  }
  /**
   * Clones the positioner while updating the `active` value. This is designed
   * for usage in frameworks like `react`.
   */


  active(isActive) {
    var filterFunction = isFunction(isActive) ? isActive : () => isActive;
    return this.clone(original => ({
      getActive: props => original.getActive(props).filter(filterFunction)
    }));
  }

}

_defineProperty(Positioner, "EMPTY", []);

/**
 * Checks if the given transaction force updates positioners.
 *
 * @param tr - the Transaction to check
 * @param key - filter for a specific key. Defaults to all.
 */
function isPositionerUpdateTransaction(tr) {
  var _tr$getMeta;

  var key = arguments.length > 1 && arguments[1] !== undefined ? arguments[1] : POSITIONER_UPDATE_ALL;

  var _ref = (_tr$getMeta = tr === null || tr === void 0 ? void 0 : tr.getMeta(POSITIONER_UPDATE_KEY)) !== null && _tr$getMeta !== void 0 ? _tr$getMeta : {},
      trKey = _ref.key;

  return trKey === key;
}
/**
 * Checks the transaction for changes or compares the state with the previous
 * state.
 *
 * Return `true` when a change is detected in the document or the selection.
 */

function hasStateChanged(props) {
  var tr = props.tr,
      state = props.state,
      previousState = props.previousState;

  if (!previousState) {
    return true;
  }

  if (tr && isPositionerUpdateTransaction(tr)) {
    return true;
  }

  if (tr) {
    return hasTransactionChanged(tr);
  }

  return !state.doc.eq(previousState.doc) || !state.selection.eq(previousState.selection);
}

/**
 * Checks that the rect is visible within the provided element.
 *
 * This is specific for the remirror editor.
 */
function isPositionVisible(rect, element) {
  var options = arguments.length > 2 && arguments[2] !== undefined ? arguments[2] : {};
  var elementRect = element.getBoundingClientRect();
  var _options$accountForPa = options.accountForPadding,
      accountForPadding = _options$accountForPa === void 0 ? false : _options$accountForPa; // Amount to increment these values by.

  var leftDelta = 0;
  var rightDelta = 0;
  var topDelta = 0;
  var bottomDelta = 0;

  if (isElementDomNode(element) && accountForPadding) {
    var paddingLeft = Number.parseFloat(getStyle(element, 'padding-left').replace('px', ''));
    var paddingRight = Number.parseFloat(getStyle(element, 'padding-right').replace('px', ''));
    var paddingTop = Number.parseFloat(getStyle(element, 'padding-top').replace('px', ''));
    var paddingBottom = Number.parseFloat(getStyle(element, 'padding-bottom').replace('px', ''));
    var borderLeft = Number.parseFloat(getStyle(element, 'border-left').replace('px', ''));
    var borderRight = Number.parseFloat(getStyle(element, 'border-right').replace('px', ''));
    var borderTop = Number.parseFloat(getStyle(element, 'border-top').replace('px', ''));
    var borderBottom = Number.parseFloat(getStyle(element, 'border-bottom').replace('px', ''));
    var verticalScrollBarWidth = element.offsetWidth - element.clientWidth;
    var horizontalScrollBarHeight = element.offsetHeight - element.clientHeight;
    leftDelta += paddingLeft + borderLeft + (element.dir === 'rtl' ? verticalScrollBarWidth : 0);
    rightDelta += paddingRight + borderRight + (element.dir === 'rtl' ? 0 : verticalScrollBarWidth);
    topDelta += paddingTop + borderTop;
    bottomDelta += paddingBottom + borderBottom + horizontalScrollBarHeight;
  }

  var containerRect = new DOMRect(elementRect.left + leftDelta, elementRect.top + topDelta, elementRect.width - rightDelta, elementRect.height - bottomDelta);

  var _iterator = _createForOfIteratorHelper([[rect.top, rect.left], [rect.top, rect.right], [rect.bottom, rect.left], [rect.bottom, rect.right]]),
      _step;

  try {
    for (_iterator.s(); !(_step = _iterator.n()).done;) {
      var _step$value = _slicedToArray(_step.value, 2),
          top = _step$value[0],
          left = _step$value[1];

      if (within(top, containerRect.top, containerRect.bottom) && within(left, containerRect.left, containerRect.right)) {
        return true;
      }
    }
  } catch (err) {
    _iterator.e(err);
  } finally {
    _iterator.f();
  }

  return false;
}
var POSITIONER_WIDGET_KEY = 'remirror-positioner-widget';
var POSITIONER_UPDATE_KEY = 'positionerUpdate';
var POSITIONER_UPDATE_ALL = '__all_positioners__';

var basePosition = {
  y: -999999,
  x: -999999,
  width: 0,
  height: 0
};

var baseRect = _objectSpread(_objectSpread({}, basePosition), {}, {
  left: -999999,
  top: -999999,
  bottom: -999999,
  right: -999999
});

var defaultAbsolutePosition = _objectSpread(_objectSpread({}, basePosition), {}, {
  rect: _objectSpread(_objectSpread({}, baseRect), {}, {
    toJSON: () => baseRect
  }),
  visible: false
});
/**
 * Creates a positioner for the current block node.
 *
 * It spans the full width and height of the block.
 */

var blockNodePositioner = Positioner.create({
  hasChanged: hasStateChanged,

  /**
   * This is only active for empty top level nodes. The data is the cursor start
   * and end position.
   */
  getActive(props) {
    var state = props.state;

    if (!isSelectionEmpty(state) || state.selection.$anchor.depth > 2) {
      return Positioner.EMPTY;
    }

    var parentNode = findParentNode({
      predicate: node => node.type.isBlock,
      selection: state
    });
    return parentNode ? [parentNode] : Positioner.EMPTY;
  },

  getPosition(props) {
    var view = props.view,
        data = props.data;
    var node = view.nodeDOM(data.pos);

    if (!isElementDomNode(node)) {
      // This should never happen.
      return defaultAbsolutePosition;
    }

    var rect = node.getBoundingClientRect();
    var editorRect = view.dom.getBoundingClientRect(); // The width and height of the current selected block node.

    var height = rect.height;
    var width = rect.width; // The top and left relative to the parent `editorRect`.

    var left = view.dom.scrollLeft + rect.left - editorRect.left;
    var top = view.dom.scrollTop + rect.top - editorRect.top;
    var visible = isPositionVisible(rect, view.dom);
    return {
      y: top,
      x: left,
      height,
      width,
      rect,
      visible
    };
  }

});
/**
 * Returns the block node position only when it is empty and the selection is
 * empty.
 */

var emptyBlockNodePositioner = blockNodePositioner.clone(_ref => {
  var _getActive = _ref.getActive;
  return {
    getActive: props => {
      var _getActive2 = _getActive(props),
          _getActive3 = _slicedToArray(_getActive2, 1),
          parentNode = _getActive3[0];

      return parentNode && isEmptyBlockNode(parentNode.node) && parentNode.node.type === getDefaultBlockNode(props.state.schema) ? [parentNode] : Positioner.EMPTY;
    }
  };
});
/**
 * Returns the position as a single pixel width for the start of the block node
 * as a position
 */

var emptyBlockNodeStartPositioner = emptyBlockNodePositioner.clone(_ref2 => {
  var _getPosition = _ref2.getPosition;
  return {
    getPosition: props => _objectSpread(_objectSpread({}, _getPosition(props)), {}, {
      width: 1
    })
  };
});
/**
 * Returns the position as a single pixel width for the end of the current block
 * node.
 */

var emptyBlockNodeEndPositioner = emptyBlockNodePositioner.clone(_ref3 => {
  var _getPosition2 = _ref3.getPosition;
  return {
    getPosition: props => {
      var _getPosition3 = _getPosition2(props),
          width = _getPosition3.width,
          left = _getPosition3.x,
          top = _getPosition3.y,
          height = _getPosition3.height;

      return _objectSpread(_objectSpread({}, _getPosition2(props)), {}, {
        width: 1,
        x: width + left,
        rect: new DOMRect(width + left, top, 1, height)
      });
    }
  };
});

function createSelectionPositioner(isActive) {
  return Positioner.create({
    hasChanged: hasStateChanged,
    getActive: props => {
      var state = props.state,
          view = props.view;

      if (!isActive(state) || !isTextSelection(state.selection)) {
        return Positioner.EMPTY;
      }

      try {
        var _state$selection = state.selection,
            head = _state$selection.head,
            anchor = _state$selection.anchor;
        return [{
          from: view.coordsAtPos(anchor),
          to: view.coordsAtPos(head)
        }];
      } catch (_unused) {
        return Positioner.EMPTY;
      }
    },

    getPosition(props) {
      var _element$offsetParent;

      var element = props.element,
          data = props.data,
          view = props.view;
      var from = data.from,
          to = data.to;
      var parent = (_element$offsetParent = element.offsetParent) !== null && _element$offsetParent !== void 0 ? _element$offsetParent : view.dom;
      var parentRect = parent.getBoundingClientRect();
      var height = Math.abs(to.bottom - from.top); // True when the selection spans multiple lines.

      var spansMultipleLines = height > from.bottom - from.top; // The position furthest to the left.

      var leftmost = Math.min(from.left, to.left); // The position nearest the top.

      var topmost = Math.min(from.top, to.top);
      var left = parent.scrollLeft + (spansMultipleLines ? to.left - parentRect.left : leftmost - parentRect.left);
      var top = parent.scrollTop + topmost - parentRect.top;
      var width = spansMultipleLines ? 1 : Math.abs(from.left - to.right);
      var rect = new DOMRect(spansMultipleLines ? to.left : leftmost, topmost, width, height);
      var visible = isPositionVisible(rect, view.dom);
      return {
        rect,
        y: top,
        x: left,
        height,
        width,
        visible
      };
    }

  });
}
/**
 * Create a position that fully capture the selected text. When the selection
 * spans multiple lines, the position is created as a box that fully captures
 * the start cursor and end cursor.
 */


var selectionPositioner = createSelectionPositioner(state => !state.selection.empty);
/**
 * This can be used to position a menu that is inline with the first character
 * of the selection. This is useful for suggestions since they should typically
 * appear while typing without a multi character selection.
 *
 * @remarks
 *
 * The menu will center itself within the selection.
 *
 * - `right` should be used to absolutely position away from the right hand edge
 *   of the screen.
 * - `left` should be used to absolutely position away from the left hand edge
 *   of the screen.
 * - `bottom` absolutely positions the element above the text selection.
 * - `top` absolutely positions the element below the text selection
 */

var cursorPositioner = createSelectionPositioner(state => state.selection.empty);
/**
 * Always render a position regardless of selection.
 */

var alwaysPositioner = createSelectionPositioner(() => true);
/**
 * Creates a position which captures the current active word. Nothing is returned
 * if no word is active.
 *
 * This is only active when the selection is empty (cursor selection)
 *
 * @remarks
 *
 * Creates a rect that wraps the nearest word.
 */

var nearestWordPositioner = selectionPositioner.clone(() => ({
  getActive: props => {
    var state = props.state,
        view = props.view;

    if (!state.selection.empty) {
      return Positioner.EMPTY;
    }

    var word = getSelectedWord(state);

    if (!word) {
      return Positioner.EMPTY;
    }

    try {
      return [{
        from: view.coordsAtPos(word.from),
        to: view.coordsAtPos(word.to)
      }];
    } catch (_unused2) {
      return Positioner.EMPTY;
    }
  }
}));

/**
 * Create a positioner for the currently selected mark
 */
function createMarkPositioner(props) {
  var type = props.type,
      _props$all = props.all,
      all = _props$all === void 0 ? false : _props$all,
      _props$onlyVisible = props.onlyVisible,
      onlyVisible = _props$onlyVisible === void 0 ? false : _props$onlyVisible;
  return Positioner.create({
    hasChanged: hasStateChanged,
    getActive: props => {
      var state = props.state,
          view = props.view;
      var selection = getTextSelection(all ? 'all' : state.selection, state.doc);

      try {
        var ranges = getMarkRanges(selection, type).map(range => {
          var from = range.from,
              to = range.to;
          var cursor = {
            from: view.coordsAtPos(from),
            to: view.coordsAtPos(to)
          };
          var visible = isPositionVisible(getCursorRect(cursor.from), view.dom) || isPositionVisible(getCursorRect(cursor.to), view.dom);
          return _objectSpread(_objectSpread({}, range), {}, {
            visible,
            cursor
          });
        });
        return onlyVisible ? ranges.filter(range => range.visible) : ranges;
      } catch (_unused3) {
        return Positioner.EMPTY;
      }
    },
    getPosition: props => {
      var _element$offsetParent2;

      var element = props.element,
          data = props.data,
          view = props.view;
      var cursor = data.cursor,
          visible = data.visible;
      var from = cursor.from,
          to = cursor.to;
      var parent = (_element$offsetParent2 = element.offsetParent) !== null && _element$offsetParent2 !== void 0 ? _element$offsetParent2 : view.dom;
      var parentRect = parent.getBoundingClientRect();
      var height = Math.abs(to.bottom - from.top); // True when the selection spans multiple lines.

      var spansMultipleLines = height > from.bottom - from.top; // The position furthest to the left.

      var leftmost = Math.min(from.left, to.left); // The position nearest the top.

      var topmost = Math.min(from.top, to.top);
      var left = parent.scrollLeft + (spansMultipleLines ? to.left - parentRect.left : leftmost - parentRect.left);
      var top = parent.scrollTop + topmost - parentRect.top;
      var width = spansMultipleLines ? 1 : Math.abs(from.left - to.right);
      var rect = new DOMRect(spansMultipleLines ? to.left : leftmost, topmost, width, height);
      return {
        rect,
        y: top,
        x: left,
        height,
        width,
        visible
      };
    }
  });
}

function getCursorRect(coords) {
  return new DOMRect(coords.left, coords.top, 1, coords.top - coords.bottom);
}

var _dec, _dec2, _dec3, _class, _class2;
var PositionerExtension = (_dec = extension({
  defaultOptions: {
    scrollDebounce: 100
  },
  customHandlerKeys: ['positioner'],
  staticKeys: ['scrollDebounce']
}), _dec2 = command(), _dec3 = helper(), _dec(_class = (_class2 = class PositionerExtension extends PlainExtension {
  constructor() {
    super(...arguments);

    _defineProperty(this, "positioners", []);

    _defineProperty(this, "onAddCustomHandler", _ref => {
      var positioner = _ref.positioner;

      if (!positioner) {
        return;
      }

      this.positioners = [...this.positioners, positioner]; // Ensure `onStateUpdate` is trigger when a positioner is added

      this.store.commands.forceUpdate();
      return () => {
        this.positioners = this.positioners.filter(handler => handler !== positioner);
      };
    });
  }

  get name() {
    return 'positioner';
  }
  /**
   * All the active positioners for the editor.
   */


  createAttributes() {
    /**
     * Add the positioner class to make the main editor container `relative` so
     * that positioners can be fixed in relation to the editor.
     */
    return {
      class: ExtensionPositionerTheme.EDITOR
    };
  }

  init() {
    this.onScroll = debounce(this.options.scrollDebounce, this.onScroll.bind(this));
  }

  createEventHandlers() {
    return {
      scroll: () => {
        this.onScroll();
        return false;
      },
      hover: _hover => {
        this.positioner(this.getBaseProps('hover', {
          hover: _hover
        }));
        return false;
      },
      contextmenu: _contextmenu => {
        this.positioner(this.getBaseProps('contextmenu', {
          contextmenu: _contextmenu
        }));
        return false;
      }
    };
  }

  onStateUpdate(update) {
    this.positioner(_objectSpread(_objectSpread({}, update), {}, {
      previousState: update.firstUpdate ? undefined : update.previousState,
      event: 'state',
      helpers: this.store.helpers
    }));
  }
  /**
   * Create a placeholder decoration which is never removed from the document.
   */


  createDecorations(state) {
    var _this$element;

    (_this$element = this.element) !== null && _this$element !== void 0 ? _this$element : this.element = this.createElement(); // Only add the decorations when there are positioners present.

    if (!this.element.hasChildNodes()) {
      return DecorationSet.empty;
    } // Use the element as the decoration which is always available at the start of the document.


    var decoration = Decoration.widget(0, this.element, {
      key: 'positioner-widget',
      side: -1,
      // TODO tests this which prevents any events from bubbling through
      stopEvent: () => true
    });
    return DecorationSet.create(state.doc, [decoration]);
  }
  /**
   * Trigger an update of positioners manually. This can be useful to update positioners when
   * the view is updated in a way that doesn't trigger a ProseMirror state change. For instance
   * when an image URL is loaded and the document is reflowed.
   *
   * @param key - Allows filtering a specific type of positioner to update. Defaults to all.
   */


  forceUpdatePositioners() {
    var key = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : POSITIONER_UPDATE_ALL;
    return _ref2 => {
      var tr = _ref2.tr,
          dispatch = _ref2.dispatch;
      dispatch === null || dispatch === void 0 ? void 0 : dispatch(tr.setMeta(POSITIONER_UPDATE_KEY, {
        key
      }));
      return true;
    };
  }
  /**
   * Get the html element which contains all the positioner elements and
   * components.
   */


  getPositionerWidget() {
    var _this$element2;

    return (_this$element2 = this.element) !== null && _this$element2 !== void 0 ? _this$element2 : this.element = this.createElement();
  }

  createElement() {
    var element = document.createElement('span');
    element.dataset.id = POSITIONER_WIDGET_KEY;
    element.setAttribute('role', 'presentation');
    return element;
  }

  triggerPositioner(positioner, update) {
    if (!positioner.hasChanged(update)) {
      // Nothing has changed so return without calling the change handler.
      return;
    }

    positioner.onActiveChanged(_objectSpread(_objectSpread({}, update), {}, {
      view: this.store.view
    }));
  }

  positioner(update) {
    var _iterator = _createForOfIteratorHelper(this.positioners),
        _step;

    try {
      for (_iterator.s(); !(_step = _iterator.n()).done;) {
        var positioner = _step.value;
        var eventIsNotSupported = !positioner.events.includes(update.event);

        if (eventIsNotSupported) {
          continue;
        }

        this.triggerPositioner(positioner, update);
      }
    } catch (err) {
      _iterator.e(err);
    } finally {
      _iterator.f();
    }
  }

  getBaseProps(event, extra) {
    var state = this.store.getState();
    var previousState = this.store.previousState;
    return _objectSpread({
      helpers: this.store.helpers,
      event,
      firstUpdate: false,
      previousState,
      state
    }, extra);
  }

  onScroll() {
    this.positioner(this.getBaseProps('scroll', {
      scroll: {
        scrollTop: this.store.view.dom.scrollTop
      }
    }));
  }

}, (_applyDecoratedDescriptor(_class2.prototype, "forceUpdatePositioners", [_dec2], Object.getOwnPropertyDescriptor(_class2.prototype, "forceUpdatePositioners"), _class2.prototype), _applyDecoratedDescriptor(_class2.prototype, "getPositionerWidget", [_dec3], Object.getOwnPropertyDescriptor(_class2.prototype, "getPositionerWidget"), _class2.prototype)), _class2)) || _class);
var positioners = {
  /**
   * Creates a rect which wraps the current selection.
   */
  selection: selectionPositioner,

  /**
   * Creates a rect for the cursor. Is inactive for
   */
  cursor: cursorPositioner,

  /**
   * Creates a positioner which always shows the position of the selection whether empty or not.
   */
  always: alwaysPositioner,

  /**
   * Creates a position which wraps the entire selected block node.
   */
  block: blockNodePositioner,

  /**
   * Creates a position which wraps the entire selected block node. This is only active when the block node is empty.
   */
  emptyBlock: emptyBlockNodePositioner,

  /**
   * Creates a position which wraps the entire selected block node. This is only active when the block node is empty.
   */
  emptyBlockStart: emptyBlockNodeStartPositioner,

  /**
   * Creates a position which wraps the entire selected block node. This is only active when the block node is empty.
   */
  emptyBlockEnd: emptyBlockNodeEndPositioner,

  /**
   * Create a rect which surrounds the nearest word.
   */
  nearestWord: nearestWordPositioner
};
/**
 * This is a helper method for getting the positioner. The props can either
 * be a named positioner or a positioner that you've created for the purpose.
 */

function getPositioner(positioner) {
  if (isString(positioner)) {
    return positioners[positioner].clone();
  }

  if (isFunction(positioner)) {
    return positioner().clone();
  }

  return positioner.clone();
}

export { POSITIONER_UPDATE_ALL, POSITIONER_UPDATE_KEY, POSITIONER_WIDGET_KEY, Positioner, PositionerExtension, alwaysPositioner, blockNodePositioner, createMarkPositioner, cursorPositioner, defaultAbsolutePosition, emptyBlockNodeEndPositioner, emptyBlockNodePositioner, emptyBlockNodeStartPositioner, getPositioner, hasStateChanged, isPositionVisible, isPositionerUpdateTransaction, nearestWordPositioner, positioners, selectionPositioner };
